import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { withForm } from '~/utils/withForm';
import { Modal } from '~/components';
import { useForm } from '~/hooks';
import { useCreateProject } from '~/mutations/project/useCreateProject';
import { useDuplicateProject } from '~/mutations/project/useDuplicateProject';
import { CreateProjectDealType, CreateProjectTypes, DevelopmentStages, PartnerTypes, PricingType, LongTermOwner } from '~/constants';
import CreateNewProjectSelectionForm from './CreateNewProjectSelectionForm';
import CreatePricingEstimateForm from './CreatePricingEstimateForm';
import CreateProjectForm from './CreateProjectForm';
import DuplicateProjectForm from './DuplicateProjectForm';
import { Project } from '~/types/project';
import CreateNewProjectDealType from './CreateNewProjectDealType';
import { DeploymentTypes } from '~/constants/deployment-type';


const ModalWrapper = styled.div`
  i.icon.close {
    background-color: red !important;
  }
`;

interface CreateNewProjectModalProps {
  closeModal: () => void;
  projectType?: string;
  portfolioId?: string;
  projectData?: Partial<Project>;
  installerId?: string;
}

interface ProjectDealTypeData {
  developmentStage?: keyof typeof DevelopmentStages;
  pricingType?: keyof typeof PricingType;
  partnerType?: keyof typeof PartnerTypes;
  longTermOwner?: keyof typeof LongTermOwner;
}

const defaults = {
  createProjectDealTypeSelection: '',
  createProjectSelection: '',
  name: '',
  addressState: '',
  pricingType: ''
};

export type FormTypes = 'CREATE_PROJECT_DEAL_TYPE' | 'PROJECT_SELECTION' | 'CREATE_PROJECT' | 'CREATE_PRICING_ESTIMATE' | 'DUPLICATE_PROJECT' | null;

const CreateNewProjectModal = withForm({ defaults }, ({
  closeModal,
  projectType,
  portfolioId,
  projectData = {},
  installerId
}: CreateNewProjectModalProps) => {
  const { formState } = useForm() as any;
  // const [currentForm, setCurrentForm] = useState<FormTypes>(projectData?.deploymentType === DeploymentTypes.CI ? 'CREATE_PROJECT_DEAL_TYPE' : 'CREATE_PROJECT');
  const [currentForm, setCurrentForm] = useState<FormTypes>('CREATE_PROJECT_DEAL_TYPE');
  const [dealTypeProjectData, setDealTypeProjectData] = useState<Partial<Project>>(projectData);
  const [hasInlineRadioField, setHasInlineRadioField] = useState(false);
  const [isDuplicatingProject, setIsDuplicatingProject] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const updateProjectData = (
    data?: Partial<Pick<ProjectDealTypeData, 'developmentStage' | 'pricingType' | 'partnerType' | 'longTermOwner'>>
  ) => {
    const isNewConstruction = formState?.createProjectDealTypeSelection === CreateProjectDealType.NEW_CONSTRUCTION;
    if (data) {
      setDealTypeProjectData((prevData) => ({
        ...prevData,
        ...data
      }));
    }
    setCurrentForm((isNewConstruction && projectData?.deploymentType === DeploymentTypes.CI) ? 'PROJECT_SELECTION' : 'CREATE_PROJECT');
  };

  const handleDealTypeSubmit = () => {
    if (formState?.createProjectDealTypeSelection === CreateProjectDealType.OPERATING_PROJECT_SALE) {
      updateProjectData({ 
        developmentStage: DevelopmentStages.OPERATING as keyof typeof DevelopmentStages,
        partnerType: PartnerTypes.OWN as keyof typeof PartnerTypes,
        longTermOwner: LongTermOwner.DEVELOPER as keyof typeof LongTermOwner
      });
    } else if (formState?.createProjectDealTypeSelection === CreateProjectDealType.TAX_CREDIT_SALE) {
      updateProjectData({ 
        pricingType: PricingType.ITC as keyof typeof PricingType, 
        partnerType: PartnerTypes.TAX_CREDITS as keyof typeof PartnerTypes
      });
    } else {
      updateProjectData();
    }
  };

  const { status: createProjectOrPricingEstimateStatus } = useCreateProject();
  const { status: duplicateProjectStatus } = useDuplicateProject();
  
  const formProps = {
    modalRef,
    portfolioId,
    closeModal,
    installerId,
    toggleFormType() {
      setIsDuplicatingProject((prev) => !prev);
      setCurrentForm((prev) => prev === 'DUPLICATE_PROJECT' ? 'CREATE_PROJECT' : 'DUPLICATE_PROJECT');
    },
  };

  useEffect(() => {
    if (isDuplicatingProject) {
      setCurrentForm('DUPLICATE_PROJECT');
    } else if (projectType === CreateProjectTypes.PROJECT) {
      setCurrentForm('CREATE_PROJECT');
    }
  }, [formState.createProjectSelection, isDuplicatingProject, projectType, currentForm]);

  useEffect(() => {
    //This adjusts the modal width for the two modals that have inline radio fields, so that the text doesn't wrap
    //This will make all the modals in the flow just slightly wider, but it looks weird if they switch back to the normal width
    //This is temporary and will be adjusted later
    if (currentForm === 'CREATE_PROJECT_DEAL_TYPE' || currentForm === 'PROJECT_SELECTION') {
      setHasInlineRadioField(true);
    } 
  }, [currentForm]);

  const isLoading = createProjectOrPricingEstimateStatus === 'loading' || duplicateProjectStatus === 'loading';
  return (
    <ModalWrapper>
      <Modal 
        ref={currentForm === 'CREATE_PROJECT' ? modalRef : null} 
        closeModal={isLoading ? undefined : closeModal}
        width = {hasInlineRadioField ? '36.5rem' : '34.5rem'}
      >
        {(() => {
          switch (currentForm) {
            case 'CREATE_PROJECT_DEAL_TYPE':
              return <CreateNewProjectDealType closeModal={closeModal} onSubmit={() => handleDealTypeSubmit()} />;
            case 'PROJECT_SELECTION':
              return (
                <CreateNewProjectSelectionForm 
                  closeModal={closeModal}
                  onSubmit={() => setCurrentForm(
                    formState.createProjectSelection === CreateProjectTypes.MARKET_QUOTE
                      ? 'CREATE_PRICING_ESTIMATE'
                      : 'CREATE_PROJECT'
                  )}
                />
              );
            case 'CREATE_PRICING_ESTIMATE':
              return <CreatePricingEstimateForm projectData={projectData} closeModal={closeModal} installerId={installerId} />;
            case 'CREATE_PROJECT':
              return <CreateProjectForm {...formProps} projectData={dealTypeProjectData} />;
            case 'DUPLICATE_PROJECT':
              return <DuplicateProjectForm {...formProps} />;
            default:
              return null;
          }
        })()}
      </Modal>
    </ModalWrapper>
  );
});

export default CreateNewProjectModal;